import { DataRecord, Row } from '@cloudio-saas/datasource-types';
import {
  BaseDrawArgs,
  drawTextCell,
  getMiddleCenterBias,
  GridCellKind,
  measureTextCached,
  SpriteMap,
  TextCell,
} from '@cloudio-saas/canvas-data-grid';
import { Theme } from '@mui/material';
import { AnyColumn, Platform } from 'componentregistry';
import { CanvasGridItem } from 'componentregistry/components/CanvasGrid/CanvasGrid';
import { DataGridItem } from 'componentregistry/components/DataGrid/DataGrid';
import { FormulaNumberColumnItem } from 'componentregistry/components/DataGrid/columns/FormulaNumberColumn/FormulaNumberColumn';
import { FormulaStringColumnItem } from 'componentregistry/components/DataGrid/columns/FormulaStringColumn/FormulaStringColumn';
import { ToolTipPosition } from 'componentregistry/registry/constants';
import { ControllerInterface } from 'controller/ControllerInterface';
import { useMemo } from 'react';
import Store from 'store/Store';
import { isFromDB } from 'store/storeTypes';
import { getErrorMessage, isNull } from 'utils/isEmpty';
import { TooltipData } from '../TooltipWrapper';
import { IconManager } from './IconsManager';
import {
  FormatColumnValueFunction,
  FormatColumnValueFunctionResult,
  IconValueWithToolTip,
  MakeCellProps,
} from './types';

export const underlineOffset = 5;

export function isEditable({
  column,
  platform,
  appUid,
  pageId,
  rowIndex,
  store,
  gridItemId,
  dataGrid,
}: {
  column: AnyColumn;
  platform: Platform;
  appUid: string;
  pageId: string;
  rowIndex: number;
  store: Store<DataRecord>;
  gridItemId: string;
  dataGrid?: boolean;
}) {
  const {
    updatable = { value: false, type: 'boolean' },
    insertable = { value: false, type: 'boolean' },
  } = column.props;
  const gridItem = dataGrid
    ? (platform.getItem(appUid, pageId, gridItemId) as DataGridItem)
    : (platform.getItem(appUid, pageId, gridItemId) as CanvasGridItem);
  const {
    insertable: gridInsertable = { value: false, type: 'boolean' },
    updatable: gridUpdatable = { value: false, type: 'boolean' },
  } = gridItem.props;
  const storeUpdatable = store.isUpdatable();
  const storeInsertable = store.isInsertable();
  const record = store.getRecord(store.getRecordIdAtRow(rowIndex));
  const fromDB = isFromDB(record);
  const updateAllowed =
    storeUpdatable &&
    fromDB &&
    (updatable.type === 'boolean'
      ? updatable.value
      : platform.evalBoolean(
          appUid,
          pageId,
          column.itemId,
          'updatable',
          {
            rowIndex,
            dsAlias: store.alias,
          },
          updatable.value,
        )) &&
    (gridUpdatable.type === 'boolean'
      ? gridUpdatable.value
      : platform.evalBoolean(
          appUid,
          pageId,
          gridItemId,
          'updatable',
          {
            rowIndex,
            dsAlias: store.alias,
          },
          gridUpdatable.value,
        ));
  const insertAllowed =
    storeInsertable &&
    !fromDB &&
    (insertable.type === 'boolean'
      ? insertable.value
      : platform.evalBoolean(
          appUid,
          pageId,
          column.itemId,
          'insertable',
          {
            rowIndex,
            dsAlias: store.alias,
          },
          insertable.value,
        )) &&
    (gridInsertable.type === 'boolean'
      ? gridInsertable.value
      : platform.evalBoolean(
          appUid,
          pageId,
          gridItemId,
          'insertable',
          {
            rowIndex,
            dsAlias: store.alias,
          },
          gridInsertable.value,
        ));

  return fromDB ? updateAllowed : insertAllowed;
}

export function makeFormulaNumberCell(
  column: FormulaNumberColumnItem,
  {
    appUid,
    pageId,
    platform,
    row,
    store,
    themeOverride,
    cursor,
  }: MakeCellProps,
): TextCell {
  const { formula } = column.props;
  const _value = platform.evalNumber(
    appUid,
    pageId,
    column.itemId,
    'formula',
    { rowIndex: row, dsAlias: store.alias },
    formula.value,
  );

  const data = isNull(_value) ? '' : String(_value);

  return {
    kind: GridCellKind.Text,
    data,
    allowOverlay: false,
    displayData: data,
    readonly: true,
    contentAlign: 'right',
    themeOverride,
    cursor,
  };
}

export function makeFormulaStringCell(
  column: FormulaStringColumnItem,
  {
    appUid,
    pageId,
    platform,
    row,
    store,
    themeOverride,
    cursor,
    contentAlign,
  }: MakeCellProps,
): TextCell {
  const { formula } = column.props;
  const _value = platform.evalIfExpressionString(
    appUid,
    pageId,
    column.itemId,
    'formula',
    { rowIndex: row, dsAlias: store.alias },
    formula,
  );

  const data = _value ?? '';

  return {
    kind: GridCellKind.Text,
    data,
    allowOverlay: false,
    displayData: data,
    readonly: true,
    contentAlign,
    themeOverride,
    cursor,
  };
}

export function useHeaderIcons(theme: Theme) {
  const textColor = theme.palette.text.secondary;
  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;
  const errorColor = theme.palette.error.light;
  const headerIcons = useMemo<SpriteMap>(() => {
    return {
      checkSquare: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm-204.686-98.059l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.248-16.379-6.249-22.628 0L184 302.745l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.25 16.379 6.25 22.628.001z" fill="${primaryColor}"/></svg>`,
      square: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm0-400H48v352h352V80z" fill="${textColor}"/></svg>`,
      minusSquare: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><defs><style>.fa-secondary{opacity:.4}</style></defs><path d="M400 32H48A48 48 0 0 0 0 80v352a48 48 0 0 0 48 48h352a48 48 0 0 0 48-48V80a48 48 0 0 0-48-48zm-32 252a12 12 0 0 1-12 12H92a12 12 0 0 1-12-12v-56a12 12 0 0 1 12-12h264a12 12 0 0 1 12 12z" class="fa-secondary" fill="${primaryLightColor}"/><path d="M368 228v56a12 12 0 0 1-12 12H92a12 12 0 0 1-12-12v-56a12 12 0 0 1 12-12h264a12 12 0 0 1 12 12z" fill="${primaryColor}"/></svg>`,
      ellipsis: ({ fgColor }) =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M64 208c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zM16 104c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48zm0 304c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48z" fill="${fgColor}"/></svg>`,
      sortDesc: ({ fgColor }) =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M312 384h-48a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h48a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8zm64-96H264a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h112a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8zM504 96H264a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h240a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8zm-64 96H264a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h176a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8zM120.44 35.51a11.94 11.94 0 0 0-16.87 0l-84 82.32a12 12 0 0 0-.09 17l5.61 5.68a11.93 11.93 0 0 0 16.91.09l54-52.74V472a8 8 0 0 0 8 8h16a8 8 0 0 0 8-8V88.08l53.94 52.35a12 12 0 0 0 16.92 0l5.64-5.66a12 12 0 0 0 0-17z" fill="${fgColor}"/></svg>`,
      sortAsc: ({ fgColor }) =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M264 320h176a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8H264a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8zm0-192h48a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8h-48a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8zm0 96h112a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8H264a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8zm240 160H264a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h240a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8zm-305.07-12.44a11.93 11.93 0 0 0-16.91-.09l-54 52.67V40a8 8 0 0 0-8-8H104a8 8 0 0 0-8 8v383.92l-53.94-52.35a12 12 0 0 0-16.92 0l-5.64 5.66a12 12 0 0 0 0 17l84.06 82.3a11.94 11.94 0 0 0 16.87 0l84-82.32a12 12 0 0 0 .09-17z" fill="${fgColor}"/></svg>`,
      filter: ({ fgColor }) =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M479.968 0H32.038C3.613 0-10.729 34.487 9.41 54.627L192 237.255V424a31.996 31.996 0 0 0 10.928 24.082l64 55.983c20.438 17.883 53.072 3.68 53.072-24.082V237.255L502.595 54.627C522.695 34.528 508.45 0 479.968 0zM288 224v256l-64-56V224L32 32h448L288 224z" fill="${fgColor}"/></svg>`,
      expanded: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M140 274c-6.6 0-12-5.4-12-12v-12c0-6.6 5.4-12 12-12h232c6.6 0 12 5.4 12 12v12c0 6.6-5.4 12-12 12H140zm364-18c0 137-111 248-248 248S8 393 8 256 119 8 256 8s248 111 248 248zm-32 0c0-119.9-97.3-216-216-216-119.9 0-216 97.3-216 216 0 119.9 97.3 216 216 216 119.9 0 216-97.3 216-216z" fill="${textColor}"/></svg>`,
      collapsed: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M384 250v12c0 6.6-5.4 12-12 12h-98v98c0 6.6-5.4 12-12 12h-12c-6.6 0-12-5.4-12-12v-98h-98c-6.6 0-12-5.4-12-12v-12c0-6.6 5.4-12 12-12h98v-98c0-6.6 5.4-12 12-12h12c6.6 0 12 5.4 12 12v98h98c6.6 0 12 5.4 12 12zm120 6c0 137-111 248-248 248S8 393 8 256 119 8 256 8s248 111 248 248zm-32 0c0-119.9-97.3-216-216-216-119.9 0-216 97.3-216 216 0 119.9 97.3 216 216 216 119.9 0 216-97.3 216-216z" fill="${textColor}"/></svg>`,
      rowSelection: () =>
        `<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M400 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm0 32c8.823 0 16 7.178 16 16v352c0 8.822-7.177 16-16 16H48c-8.822 0-16-7.178-16-16V80c0-8.822 7.178-16 16-16h352m-34.301 98.293l-8.451-8.52c-4.667-4.705-12.265-4.736-16.97-.068l-163.441 162.13-68.976-69.533c-4.667-4.705-12.265-4.736-16.97-.068l-8.52 8.451c-4.705 4.667-4.736 12.265-.068 16.97l85.878 86.572c4.667 4.705 12.265 4.736 16.97.068l180.48-179.032c4.704-4.667 4.735-12.265.068-16.97z" fill="${textColor}"/></svg>`,
      angleDown: () =>
        `<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M119.5 262.9L3.5 145.1c-4.7-4.7-4.7-12.3 0-17l7.1-7.1c4.7-4.7 12.3-4.7 17 0L128 223.3l100.4-102.2c4.7-4.7 12.3-4.7 17 0l7.1 7.1c4.7 4.7 4.7 12.3 0 17L136.5 263c-4.7 4.6-12.3 4.6-17-.1zm17 128l116-117.8c4.7-4.7 4.7-12.3 0-17l-7.1-7.1c-4.7-4.7-12.3-4.7-17 0L128 351.3 27.6 249.1c-4.7-4.7-12.3-4.7-17 0l-7.1 7.1c-4.7 4.7-4.7 12.3 0 17l116 117.8c4.7 4.6 12.3 4.6 17-.1z" fill="${textColor}"/></svg>`,
      angleUp: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M136.5 249.1l116 117.8c4.7 4.7 4.7 12.3 0 17l-7.1 7.1c-4.7 4.7-12.3 4.7-17 0L128 288.7 27.6 390.9c-4.7 4.7-12.3 4.7-17 0l-7.1-7.1c-4.7-4.7-4.7-12.3 0-17l116-117.8c4.7-4.6 12.3-4.6 17 .1zm-17-128L3.5 238.9c-4.7 4.7-4.7 12.3 0 17l7.1 7.1c4.7 4.7 12.3 4.7 17 0L128 160.7l100.4 102.2c4.7 4.7 12.3 4.7 17 0l7.1-7.1c4.7-4.7 4.7-12.3 0-17L136.5 121c-4.7-4.6-12.3-4.6-17 .1z" fill="${textColor}"/></svg>`,
      caretRight: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zm368 0c0 6.7-2.8 13-7.7 17.6l-112 104c-7 6.5-17.2 8.2-25.9 4.4s-14.4-12.5-14.4-22l0-208c0-9.5 5.7-18.2 14.4-22s18.9-2.1 25.9 4.4l112 104c4.9 4.5 7.7 10.9 7.7 17.6z" fill="${textColor}"/></svg>`,
      caretDown: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"> <path d="M256 0a256 256 0 1 0 0 512A256 256 0 1 0 256 0zm0 368c-6.7 0-13-2.8-17.6-7.7l-104-112c-6.5-7-8.2-17.2-4.4-25.9s12.5-14.4 22-14.4l208 0c9.5 0 18.2 5.7 22 14.4s2.1 18.9-4.4 25.9l-104 112c-4.5 4.9-10.9 7.7-17.6 7.7z" fill="${textColor}"/></svg>`,
      delete: () =>
        `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path d="M432 64C440.8 64 448 71.16 448 80C448 88.84 440.8 96 432 96H413.7L388.2 452.6C385.9 486.1 357.1 512 324.4 512H123.6C90.01 512 62.15 486.1 59.75 452.6L34.29 96H16C7.164 96 0 88.84 0 80C0 71.16 7.164 64 16 64H111.1L137 22.56C145.8 8.526 161.2 0 177.7 0H270.3C286.8 0 302.2 8.526 310.1 22.56L336.9 64H432zM177.7 32C172.2 32 167.1 34.84 164.2 39.52L148.9 64H299.1L283.8 39.52C280.9 34.84 275.8 32 270.3 32H177.7zM381.6 96H66.37L91.67 450.3C92.87 467 106.8 480 123.6 480H324.4C341.2 480 355.1 467 356.3 450.3L381.6 96z" fill="${errorColor}"/></svg>`,
    };
  }, [primaryColor, textColor, primaryLightColor, errorColor]);
  return headerIcons;
}

export function formatColumnValue(props: {
  appUid: string;
  controller: ControllerInterface;
  formatFunction: string;
  viewAttribute: string;
  rawValue: unknown;
  value?: string | null;
  record: Row<unknown>;
  pageId: string;
  column: AnyColumn;
  platform: Platform;
  store: Store<DataRecord>;
  row: number;
  theme: Theme;
}): string | FormatColumnValueFunctionResult {
  const {
    appUid,
    controller,
    formatFunction,
    viewAttribute,
    rawValue,
    record,
    pageId,
    column,
    platform,
    store,
    row,
    value,
    theme,
  } = props;
  const fn = controller[formatFunction] as FormatColumnValueFunction;
  let displayData: FormatColumnValueFunctionResult | string = '';
  if (typeof fn !== 'function') {
    displayData = `Function [${formatFunction}] not found!`;
    platform.showError(displayData);
  } else {
    try {
      const result = fn({
        appUid,
        viewAttribute,
        value: value ?? String(rawValue ?? ''),
        rawValue,
        record,
        functionName: formatFunction,
        pageId,
        itemId: column.itemId,
        platform,
        store,
        row,
        theme,
      }) as string | FormatColumnValueFunctionResult;
      displayData = result;
      if (typeof result === 'object' && result !== null && 'value' in result) {
        return result;
      }
      if (typeof displayData !== 'string') {
        displayData = String(displayData ?? '');
      }
    } catch (e) {
      displayData = `Function [${formatFunction}] error: ${getErrorMessage(
        e,
      )}!`;
      platform.showError(displayData);
    }
  }
  return displayData;
}

export function drawTextWithIcon(props: {
  args: BaseDrawArgs;
  contentAlign?: 'left' | 'right' | 'center' | undefined;
  displayValue: string;
  hasClick: boolean;
  icon?: IconValueWithToolTip;
  iconManager: IconManager;
  itemId: string;
  offsetX?: number;
  platform: Platform;
  setTooltipProps: (
    value: React.SetStateAction<TooltipData | undefined>,
  ) => TooltipData | undefined;
  hoverRef: React.MutableRefObject<{
    itemId: string;
    x: number;
    y: number;
  }>;
}): void {
  const {
    args,
    contentAlign,
    displayValue,
    hasClick,
    hoverRef,
    icon,
    iconManager,
    itemId,
    offsetX = 0,
    platform,
    setTooltipProps,
  } = props;
  const { ctx, rect, theme: t, hoverX, hoverY, row, col, hoverAmount } = args;
  const { x, y, height } = rect;
  const iconSize = icon?.iconSize ?? 16;
  let drawX = x + offsetX;
  const xPad = t.cellHorizontalPadding;

  if (icon) {
    drawX += xPad;
    iconManager.drawSprite(
      icon.iconName || 'question',
      icon.iconType || 'light',
      ctx,
      drawX,
      y + (height - iconSize) / 2,
      iconSize,
      t,
      [col, row],
      icon.color || '',
      platform,
    );

    if (hoverAmount > 0 && hoverX && hoverY && icon.tooltip) {
      const x1 = drawX - x;
      const x2 = drawX - x + iconSize;
      const y1 = (height - iconSize) / 2;
      const y2 = (height - iconSize) / 2 + iconSize;
      if (hoverX > x1 && hoverX < x2 && hoverY > y1 && hoverY < y2) {
        if (hoverRef.current.itemId !== itemId) {
          hoverRef.current.itemId = itemId;
          let tooltipText = '';
          let toolTipPosition: ToolTipPosition = 'top';
          let toolTipMaxWidth = 600;
          if (typeof icon.tooltip === 'string') {
            tooltipText = icon.tooltip;
          } else {
            tooltipText = icon.tooltip.text;
            toolTipPosition = icon.tooltip.position || 'top';
            toolTipMaxWidth = icon.tooltip.maxWidth || 600;
          }
          setTooltipProps({
            height: iconSize,
            width: iconSize,
            x: hoverRef.current.x + drawX - x,
            y: hoverRef.current.y + (height - iconSize) / 2,
            itemId: 'x',
            toolTip: { type: 'text', value: tooltipText },
            toolTipMaxWidth,
            toolTipPosition,
            recordId: 'x',
            rowIndex: row,
            error: false,
          });
        }
      } else {
        hoverRef.current.itemId = '';
        setTooltipProps(undefined);
      }
    } else {
      hoverRef.current.itemId = '';
      setTooltipProps(undefined);
    }

    drawX += iconSize;
  }
  drawTextCell(
    drawX === x
      ? args
      : {
          ...args,
          rect: {
            ...args.rect,
            x: drawX,
            width: args.rect.width - (drawX - x),
          },
        },
    displayValue,
    contentAlign,
  );

  if (hasClick && hoverX && hoverY) {
    const font = `${t.baseFontStyle} ${t.fontFamily}`;
    const middleCenterBias = getMiddleCenterBias(ctx, font);
    const metrics = measureTextCached(displayValue, ctx);
    const drawY = rect.y + rect.height / 2 + middleCenterBias;
    const bottom = Math.floor(drawY + underlineOffset) + 0.5;
    drawX += xPad;
    if (contentAlign === 'right') {
      drawX = rect.x + rect.width - xPad - metrics.width;
    } else if (contentAlign === 'center') {
      drawX = x + rect.width / 2 - metrics.width / 2;
    }
    ctx.beginPath();
    ctx.moveTo(drawX, bottom);
    ctx.lineTo(drawX + metrics.width, bottom);
    ctx.strokeStyle = t.textDark;
    ctx.stroke();
  }
}
